import { SessionContext } from '@contexts/session-context';
import { Feature } from '@enums/feature.enum';
import MenuItem from '@navigation/menu-item';
import {
  ButtonType,
  Icon,
  ToolTip,
  UpgradePopup,
  useTranslation,
  useInternalEvents,
} from '@testsigma/atoms';
import classNames from 'classnames';
import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReportAgentContextProvider } from '@modules/agent/report-agent';

import { PrimaryMenuItemType } from '.';
import HelpNavMenu from '../help-nav-menu';
import { UsageDetails } from '../usage-details';
import AddonCustomFieldMenu from '../addon-custom-function-menu';
import { OPEN_USAGE_DETAILS } from '@constants/events';

type actionsType = {
  type: ButtonType;
  content: string;
  callbackhandler: () => void;
  closePopup: boolean;
  isDisabled: boolean;
};

const AdditionalMenu = ({
  disableMenu,
  setIsSidebarhovered,
  setIsModelOpen,
  show,
}: {
  disableMenu: boolean;
  setIsSidebarhovered: (flag: boolean) => void;
  setIsModelOpen: (flag: boolean) => void;
  show: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useContext(SessionContext);
  const [isOpenUsageDetails, setIsOpenUsageDetails] =
    React.useState<boolean>(false);

  const MENU: PrimaryMenuItemType[] = [
    {
      lIcon: 'agents',
      displayText: 'left.nav.agent',
      url: 'agents',
      urlMatcherRegex: new RegExp(/agents/g),
      isBorder: false,
      isChild: null,
      getMissingFeature: session.hasFeature(Feature.HYBRID_EXECUTIONS)
        ? true
        : false,
      featureName: Feature.HYBRID_EXECUTIONS,
      displayLink: true,
    },
  ];
  if (!session.hasFeature(Feature.CUSTOM_FUNCTIONS_LEAP_UI)) {
    MENU.push({
      lIcon: 'addons',
      displayText: 'left.nav.addons',
      url: 'addons',
      urlMatcherRegex: new RegExp(/addons/g),
      isBorder: false,
      isChild: null,
      getMissingFeature: session.hasFeature(Feature.TESTSIGMA_SDK),
      displayLink: true,
    });
  }

  const handleUsageModalOpen = (flag: boolean) => {
    setIsOpenUsageDetails(flag);
    setIsSidebarhovered(false);
    setIsModelOpen(flag);
  };

  const actions: actionsType[] = [
    {
      type: 'primary',
      content: t('common.proceed'),
      callbackhandler: () => navigate('/settings/billing'),
      closePopup: true,
      isDisabled: !session?.user?.isAdmin,
    },
    {
      type: 'secondary',
      content: t('integrations.common.cancel'),
      callbackhandler: () => {},
      closePopup: true,
      isDisabled: false,
    },
  ];

  const getUpgardeModalContent = useCallback(
    (featureName: Feature) => {
      return (
        <div className='w-125 flex-wrap flex'>
          <h3 role='heading' className={'text-xl pb-4 font-semibold'}>
            {t('common.plan_upgrade.title')}
          </h3>
          <div className='text-secondary-500'>
            <p className='mb-2 text-sm'>
              {t(`common.upgrade.${featureName}.message`)}
            </p>
            <p className='text-sm'>
              {!session?.user?.isAdmin && t('common.not_admin.message')}
            </p>
          </div>
        </div>
      );
    },
    [session],
  );

  useInternalEvents([OPEN_USAGE_DETAILS], () => handleUsageModalOpen(true));

  return (
    <div
      className={classNames('flex w-full justify-between items-center px-0.5', {
        'pointer-events-none opacity-50': disableMenu,
        hidden: !show,
      })}
    >
      {MENU.map((item: PrimaryMenuItemType) => (
        <ToolTip
          key={item.lIcon}
          content={t(
            item?.getMissingFeature
              ? item?.displayText
              : 'common.upgrade.title',
          )}
          align='center'
          placement='top'
          className='inline-flex overflow-hidden justify-center border-[0.5px] rounded-md p-1.5 border-secondary-100 shadow mr-2 hover:!bg-secondary-50 text-unique-800 cursor-pointer !bg-[#FCFDFD]'
        >
          <UpgradePopup
            isAllowed={item?.getMissingFeature ?? true}
            iconClassName='text-warning-500'
            modalContent={getUpgardeModalContent(item?.featureName)}
            actions={actions}
          >
            <MenuItem
              isLeftIcon={item?.lIcon}
              itemKey={item?.displayText}
              navUrl={item?.url}
              showWithText={false}
              urlMatcherRegex={item?.urlMatcherRegex}
              iconOnlyItem
              displayLink={item?.displayLink}
            >
              {item.isChild}
            </MenuItem>
          </UpgradePopup>
        </ToolTip>
      ))}
      {session.hasFeature(Feature.CUSTOM_FUNCTIONS_LEAP_UI) && (
        <AddonCustomFieldMenu />
      )}
      <ToolTip
        content={t('left.nav.usage_details')}
        placement='top'
        align='center'
        className='inline-flex overflow-hidden justify-center border-[0.5px] rounded-md p-1.5 border-secondary-100 shadow mr-2 hover:!bg-secondary-50 text-unique-800 cursor-pointer !bg-[#FCFDFD]'
      >
        <div
          onClick={() => {
            setIsOpenUsageDetails(true);
            setIsModelOpen(true);
          }}
          className='flex justify-center text-unique-800 cursor-pointer'
          id='usageDetail'
        >
          <Icon name='donut-large' className='text-[20px]' />
        </div>
      </ToolTip>
      <ToolTip
        content={t('left.nav.what_s_new')}
        placement='top'
        align='center'
        className='inline-flex overflow-hidden justify-center border-[0.5px] rounded-md p-1.5 border-secondary-100 shadow mr-2 hover:!bg-secondary-50 text-unique-800 cursor-pointer !bg-[#FCFDFD]'
      >
        <div className='flex justify-center text-unique-800'>
          <MenuItem
            isLeftIcon='redeem'
            itemKey='left.nav.what_s_new'
            showWithText={false}
            dataClick={true}
            idValue='beamer-trigger'
            iconOnlyItem
          />
        </div>
      </ToolTip>

      <HelpNavMenu
        setIsModelopen={(flag) => {
          setIsModelOpen(flag);
          setIsSidebarhovered(flag);
        }}
      />
      {isOpenUsageDetails && (
        <ReportAgentContextProvider>
          <UsageDetails
            isUsageModalOpen={isOpenUsageDetails}
            setIsUsageModalOpen={(flag: boolean) => handleUsageModalOpen(flag)}
          />
        </ReportAgentContextProvider>
      )}
    </div>
  );
};

export { AdditionalMenu };
